import { useId, useLayoutEffect, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { BuscaRemota, LINHA_DE_GRUPO, Rotulo } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { ErroRequisicao, get, mensagemDeErro, post } from '../../nucleo/api';
import { focoPerdido } from '../../nucleo/foco';
import { documentoFormatado, nomePessoa, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { invalidarVinculos } from './crm-comum';

/**
 * Filiais junto com a matriz (cadastros 0441): a lista de empresas a vincular, o bloco "Esta empresa tem filiais?" de Criar empresa e o
 * resultado do vínculo (os cartões da ficha estão em `CrmFiliaisCartoes.tsx`). O vínculo vai por `POST /cadastros/parceiros/:id/filiais`
 * (`cadastros.editar`). Pedido de 04/11: a filial nova nasce pelo formulário completo da empresa ("Criar filial"), então a lista só tem empresas que já existem.
 */
export const LIMITE_DA_LISTA = 20;
export const MAX_FILIAIS_DA_MATRIZ = 200;

export interface ItemFilial {
  id: string;
  nome: string;
  documento: string | null;
  /** Por que o vínculo falhou. */
  motivo?: string;
  /** Rodada rfil-3 (SPEC-B1): por que a exclusão falhou, separado: a pergunta só diz que não excluiu depois de tentar. */
  motivo_exclusao?: string;
  /** Cadastrada neste fluxo pelo Criar filial: só estas podem ser excluídas ao desistir; a da busca só sai da lista. */
  criada?: boolean;
}

/** Acrescenta sem repetir e sem passar do limite. Função pura. */
export function comFilial(lista: ItemFilial[], item: ItemFilial, limite: number = LIMITE_DA_LISTA): ItemFilial[] {
  return lista.some((x) => x.id === item.id) || lista.length >= limite ? lista : [...lista, item];
}

export const filialDaBusca = (i: Record<string, unknown>): ItemFilial => ({ id: String(i.id), nome: String(i.nome), documento: typeof i.documento === 'string' ? i.documento : null });

const soDigitos = (v: string | null | undefined) => String(v ?? '').replace(/\D/g, '');

/** A filial da lista com o CNPJ digitado na matriz, que o cadastro não aceitaria como duas empresas diferentes. Função pura. */
export const filialComODocumento = (lista: ItemFilial[], documento: string | null | undefined) => {
  const doc = soDigitos(documento);
  return doc ? lista.find((f) => soDigitos(f.documento) === doc) : undefined;
};

/**
 * O que a filial herda da matriz naquele momento, já escolhido e editável no formulário: a mesma carteira de quem cadastrou. Rodada rfil-3
 * (M1): vai junto o espaço que resta na lista, porque o formulário nunca grava empresa que não caberia nela.
 */
export const herancaDaMatriz = (responsaveis: string[], segmentos: string[], tamanho: number, limite: number) => ({ responsaveis, segmentos, espaco: Math.max(limite - tamanho, 0), limite });

export interface FalhaDaFilial {
  nome: string;
  motivo: string;
}

/** Vincula uma de cada vez, na ordem da lista: uma falha não impede as outras. Devolve o que faltou, com o id para quem mantém a lista. */
export async function vincularFiliais(matrizId: string, lista: ItemFilial[]): Promise<(FalhaDaFilial & { id: string })[]> {
  const falhas: (FalhaDaFilial & { id: string })[] = [];
  for (const f of lista) {
    try {
      await post(`/cadastros/parceiros/${matrizId}/filiais`, { filial_id: f.id });
    } catch (e) {
      falhas.push({ id: f.id, nome: f.nome, motivo: mensagemDeErro(e) });
    }
  }
  return falhas;
}

const motivoDaLinha = (f: ItemFilial) => textoLivre(f.motivo_exclusao ?? f.motivo);

/** Nome e CNPJ de cada empresa, com o botão de tirar (não apaga a empresa). */
export function ListaDeFiliais({ itens, aoTirar, ocupado, reservas = [] }: { itens: ItemFilial[]; aoTirar?: (id: string) => void; ocupado?: boolean; reservas?: string[] }) {
  const { t } = useTranslation('cadastros');
  // Rodada rfil-3 (VIS-B1): o foco vai à linha que ficou no lugar, no mesmo desenho em que a linha sai (com ou sem a pergunta no meio). Depois disso
  // a camada já teria posto o foco em outro botão, e o `recuperarFoco` não o tira de lá.
  const ul = useRef<HTMLUListElement>(null);
  const posicao = useRef<number | null>(null);
  const tamanho = useRef(itens.length);
  useLayoutEffect(() => {
    const saiu = itens.length < tamanho.current;
    tamanho.current = itens.length;
    if (!saiu || posicao.current === null) return;
    const linhas = ul.current?.querySelectorAll<HTMLElement>('[data-tirar-da-lista]');
    const alvo = linhas?.length ? linhas[Math.min(posicao.current, linhas.length - 1)] : reservas.map((r) => document.querySelector<HTMLElement>(r)).find(Boolean);
    posicao.current = null;
    alvo?.focus();
  }, [itens.length]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!itens.length) return null;
  const tirar = (id: string, i: number) => {
    posicao.current = i;
    aoTirar?.(id);
  };
  return (
    <ul ref={ul} className="flex min-w-0 flex-col gap-2" data-lista-filiais="">
      {itens.map((f, i) => (
        <li key={f.id} className="flex min-w-0 items-start gap-2 rounded-lg border border-borda p-2 text-sm" data-item-filial={f.id}>
          <div className="min-w-0 flex-1">
            <p className="font-medium break-words text-tinta-1">{nomePessoa(f.nome)}</p>
            <p className="numero text-xs text-tinta-3">{f.documento ? documentoFormatado(f.documento) : '-'}</p>
            {motivoDaLinha(f) && (
              <p role="alert" className="mt-1 text-xs break-words text-perigo">
                {motivoDaLinha(f)}
              </p>
            )}
          </div>
          {aoTirar && <Botao variante="fantasma" tamanho="sm" ocupado={ocupado} aria-label={t('crm.filiais.tirarDaLista', { nome: nomePessoa(f.nome) })} title={t('crm.filiais.tirar')} icone={<X className="size-4" />} onClick={() => tirar(f.id, i)} data-tirar-da-lista={f.id} />}
        </li>
      ))}
    </ul>
  );
}

/** Rodada rfil-4: o limite da lista, ou o da matriz quando é ele que vale ali (aí vincular as da lista não abre espaço). */
export function textoListaCheia(t: (chave: string, opcoes?: Record<string, unknown>) => string, limite: number) {
  if (limite >= LIMITE_DA_LISTA) return t('crm.filiais.listaCheia', { count: limite });
  return limite > 0 ? t('crm.filiais.listaNoTeto', { count: limite }) : t('crm.filiais.noTeto');
}

/** Rodada rfil-6 (VIS-B2): o Salvar recusado com a lista cheia, que diz que nada foi gravado. */
export function textoNaoSalva(t: (chave: string, opcoes?: Record<string, unknown>) => string, limite: number) {
  return limite >= LIMITE_DA_LISTA ? t('crm.filiais.naoSalvaLista', { n: limite }) : t('crm.filiais.naoSalvaTeto');
}

/** Rodada rfil-6: "X de Y" concorda com o total; o resto da frase, com as que faltam. */
export function textoParcial(t: (chave: string, opcoes?: Record<string, unknown>) => string, feitas: number, total: number) {
  return `${t('crm.filiais.parcial', { count: total, feitas, total })} ${t('crm.filiais.parcialResto', { count: total - feitas })}`;
}

/** Rodada rfil-3 (VIS-B2): a última escolha tira a busca, que tinha o foco; o aviso nasce no mesmo desenho e fica com ele. */
export function AvisoListaCheia({ limite }: { limite: number }) {
  const { t } = useTranslation('cadastros');
  const ref = useRef<HTMLParagraphElement>(null);
  useLayoutEffect(() => {
    if (focoPerdido(document.activeElement, document.body)) ref.current?.focus();
  }, []);
  return (
    <p ref={ref} tabIndex={-1} className="text-xs text-tinta-3 focus:outline-none" data-lista-cheia="">
      {textoListaCheia(t, limite)}
    </p>
  );
}

/** Rodada rfil-4 (VIS-B2): com a camada aberta, as que só saíram da lista são ditas aqui: o aviso global cobriria os botões dela. */
export function AvisoPreservadas({ texto }: { texto: string | null }) {
  if (!texto) return null;
  return (
    <p role="status" className="text-sm break-words text-tinta-2" data-filiais-preservadas="">
      {texto}
    </p>
  );
}

/** "Esta empresa tem filiais?" em Criar empresa: busca de empresa já cadastrada, Adicionar empresa e a lista; o vínculo só acontece ao salvar a matriz. */
export function BlocoFiliais({
  marcado,
  aoMarcar,
  itens,
  aoEscolher,
  aoTirar,
  aoAdicionar,
  erro,
  ocupado,
  aviso = null,
}: {
  marcado: boolean;
  aoMarcar: (v: boolean) => void;
  itens: ItemFilial[];
  aoEscolher: (item: ItemFilial) => void;
  aoTirar: (id: string) => void;
  /** Sem `cadastros.criar`, sem o botão. */
  aoAdicionar?: () => void;
  erro?: string | null;
  ocupado?: boolean;
  aviso?: string | null;
}) {
  const { t } = useTranslation('cadastros');
  const id = useId();
  const cheia = itens.length >= LIMITE_DA_LISTA;
  return (
    <div className={`flex min-w-0 flex-col gap-3 ${LINHA_DE_GRUPO}`} data-filiais-formulario="">
      <label className="flex min-h-11 cursor-pointer items-center gap-2 text-sm font-medium text-tinta-2 md:min-h-9">
        <input type="checkbox" data-tem-filiais="" checked={marcado} disabled={ocupado} onChange={(e) => aoMarcar(e.target.checked)} />
        {t('crm.filiais.tem')}
      </label>
      <AvisoPreservadas texto={aviso} />
      {marcado && (
        <>
          {cheia ? (
            <AvisoListaCheia limite={LIMITE_DA_LISTA} />
          ) : (
            <>
              <Rotulo id={id} rotulo={t('crm.filiais.empresa')}>
                <BuscaRemota id={id} limparAoEscolher def={{ caminho: '/cadastros/parceiros', params: { papel: 'empresa', elegivel_filial: 'true' }, rotulo: (x) => String(x.nome), pessoa: true }} valor={null} aoMudar={(_v, item) => item && aoEscolher(filialDaBusca(item))} />
              </Rotulo>
              {aoAdicionar && (
                <Botao className="self-start" ocupado={ocupado} icone={<Plus className="size-4" />} onClick={aoAdicionar} data-adicionar-empresa-filial="">
                  {t('crm.filiais.adicionarEmpresa')}
                </Botao>
              )}
            </>
          )}
          {erro && (
            <p role="alert" tabIndex={-1} className="text-sm text-perigo focus:outline-none" data-erro-filiais="">
              {erro}
            </p>
          )}
          <ListaDeFiliais itens={itens} aoTirar={aoTirar} ocupado={ocupado} reservas={['[data-adicionar-empresa-filial]', '[data-tem-filiais]']} />
        </>
      )}
    </div>
  );
}

/**
 * A exclusão que o cadastro de empresas já tem (Inativar, `cadastros.arquivar`), uma de cada vez. Devolve o motivo de cada uma que o servidor barrou.
 * Rodada rfil-3: `criada` é de quando entrou na lista. Relê antes (SEG-B3): a que depois ganhou matriz ou entrou em mescla não é mais só o que esta
 * pessoa criou, então não se arquiva: volta em `preservadas` e só sai da lista. Já arquivada conta como excluída (ROB-B3): a resposta da tentativa anterior pode ter se perdido.
 */
export async function excluirFiliais(lista: ItemFilial[]): Promise<{ motivos: Map<string, string>; preservadas: ItemFilial[] }> {
  const motivos = new Map<string, string>();
  const preservadas: ItemFilial[] = [];
  for (const f of lista) {
    try {
      const agora = await get<{ matriz_id?: string | null; mesclado_em?: string | null; recebeu_mescla?: boolean }>(`/cadastros/parceiros/${f.id}`);
      if (agora.matriz_id || agora.mesclado_em || agora.recebeu_mescla) {
        preservadas.push(f);
        continue;
      }
      await post(`/cadastros/parceiros/${f.id}/arquivar`, {});
    } catch (e) {
      if (!(e instanceof ErroRequisicao && e.corpo.code === 'parceiro.ja_arquivado')) motivos.set(f.id, mensagemDeErro(e));
    }
  }
  return { motivos, preservadas };
}

const temMotivoDeExclusao = (f: ItemFilial) => !!f.motivo_exclusao;

/** Pergunta ao desistir de filiais criadas no fluxo: excluir ou manter salvas. Fechar sem escolher não faz nada. Sem a permissão, só manter. */
export function PerguntaDasFiliais({ itens, mista, podeExcluir, ocupado, aviso, aoExcluir, aoManter, aoFechar }: { itens: ItemFilial[]; /** Quantas da lista vieram da busca e só saem dela. */ mista: number; podeExcluir: boolean; ocupado: boolean; aviso: string | null; aoExcluir: () => void; aoManter: () => void; aoFechar: () => void }) {
  const { t } = useTranslation('cadastros');
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('crm.filiais.perguntaTitulo', { count: itens.length })}
      rodape={
        <>
          <Botao ocupado={ocupado} onClick={aoManter} data-manter-filiais="">
            {t('crm.filiais.manter', { count: itens.length })}
          </Botao>
          {podeExcluir && (
            <Botao variante="perigo" carregando={ocupado} onClick={aoExcluir} data-excluir-filiais="">
              {t('crm.filiais.excluir')}
            </Botao>
          )}
        </>
      }
    >
      <div className="flex min-w-0 flex-col gap-3 text-sm text-tinta-2" data-pergunta-filiais="">
        <p>{t(podeExcluir ? 'crm.filiais.perguntaTexto' : 'crm.filiais.semPermissaoExcluir', { count: itens.length })}</p>
        {mista > 0 && <p data-pergunta-mista="">{t('crm.filiais.perguntaMista', { count: mista })}</p>}
        {itens.some(temMotivoDeExclusao) && <p role="alert" className="text-perigo">{t('crm.filiais.falhaExcluir', { count: itens.filter(temMotivoDeExclusao).length })}</p>}
        <AvisoPreservadas texto={aviso} />
        <ListaDeFiliais itens={itens} />
      </div>
    </Modal>
  );
}

/**
 * A pergunta ao desistir de empresas criadas pelo Criar filial, a mesma em Criar empresa e no pop-up da ficha: Excluir (a exclusão que já existe)
 * ou Manter salvas. A da busca só sai da lista, sem pergunta; fechar a pergunta não muda nada. As excluídas saem da lista; as barradas ficam com o motivo.
 */
export function usePerguntaDasFiliais(lista: ItemFilial[], setLista: Dispatch<SetStateAction<ItemFilial[]>>) {
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation('cadastros');
  const [pergunta, setPergunta] = useState<{ ids: string[]; mista: number; fecha: boolean; concluir: () => void } | null>(null);
  // Rodada rfil-4 (VIS-B2): os nomes crus, e a máscara do modo de ocultar só ao desenhar. Saem no aviso global só quando a camada fecha.
  const [preservadas, setPreservadas] = useState<string[]>([]);
  const textoDe = (nomes: string[]) => (nomes.length ? t('crm.filiais.preservadas', { count: nomes.length, nomes: nomes.map((n) => nomePessoa(n)).join(', ') }) : null);
  const [excluindo, setExcluindo] = useState(false);
  const emCurso = useRef(false);
  /** `fecha`: concluir fecha a camada de quem chama (a gaveta ou o pop-up). */
  const perguntarOu = (alvos: ItemFilial[], todaALista: boolean, concluir: () => void, fecha = false) => {
    const criadas = alvos.filter((f) => f.criada);
    setPreservadas([]);
    if (!criadas.length) return concluir();
    setPergunta({ ids: criadas.map((f) => f.id), mista: todaALista ? alvos.length - criadas.length : 0, fecha, concluir });
  };
  const responder = (p: NonNullable<typeof pergunta>, nomes: string[]) => {
    setPergunta(null);
    const texto = p.fecha ? textoDe(nomes) : null;
    if (texto) toast.info(texto);
    setPreservadas(p.fecha ? [] : nomes);
    p.concluir();
  };
  const excluir = async () => {
    if (!pergunta || emCurso.current) return;
    emCurso.current = true;
    setExcluindo(true);
    try {
      const r = await excluirFiliais(lista.filter((f) => pergunta.ids.includes(f.id)));
      const { motivos } = r;
      // Rodada rfil-4 (ROB-B3): sem esperar a releitura, que prendia a pergunta com tudo já arquivado. Quem some aqui é só empresa da lista ou da busca, sem formulário de edição aberto em cima dela.
      void qc.invalidateQueries({ queryKey: ['/cadastros/parceiros'] });
      void qc.invalidateQueries({ queryKey: ['crm'] });
      void invalidarVinculos(qc);
      // Junto com o fim da pergunta: a linha sai e o foco vai à do lugar já sem a camada por cima.
      setLista((x) => x.filter((f) => !pergunta.ids.includes(f.id) || motivos.has(f.id)).map((f) => (motivos.has(f.id) ? { ...f, motivo_exclusao: motivos.get(f.id) } : f)));
      const nomes = [...preservadas, ...r.preservadas.map((f) => f.nome)];
      if (!motivos.size) return responder(pergunta, nomes);
      setPreservadas(nomes);
      setPergunta({ ...pergunta, ids: pergunta.ids.filter((id) => motivos.has(id)) });
    } finally {
      emCurso.current = false;
      setExcluindo(false);
    }
  };
  const elemento = pergunta && (
    <PerguntaDasFiliais
      itens={lista.filter((f) => pergunta.ids.includes(f.id))}
      mista={pergunta.mista}
      podeExcluir={s.pode('cadastros.arquivar')}
      ocupado={excluindo}
      aviso={textoDe(preservadas)}
      aoExcluir={() => void excluir()}
      aoManter={() => responder(pergunta, preservadas)}
      aoFechar={() => {
        if (!emCurso.current) setPergunta(null);
      }}
    />
  );
  return { perguntarOu, aberta: !!pergunta, elemento, aviso: pergunta ? null : textoDe(preservadas), limparAviso: () => setPreservadas([]) };
}

/** Rodada rcn-6 (ROB-B2): as falhas por motivo, para muitas linhas caberem na tela. Função pura. */
export function falhasPorMotivo(falhas: FalhaDaFilial[]): { motivo: string; nomes: string[] }[] {
  const grupos = new Map<string, { motivo: string; nomes: string[] }>();
  for (const f of falhas) {
    const motivo = f.motivo.replace(/\.+$/, '');
    const g = grupos.get(motivo);
    if (g) g.nomes.push(f.nome);
    else grupos.set(motivo, { motivo, nomes: [f.nome] });
  }
  return [...grupos.values()];
}

/** O que faltou das filiais, depois de a matriz gravar: fica na tela até a pessoa fechar, com rolagem, em vez de um aviso que some. */
export function ResultadoDasFiliais({ falhas, total, aoFechar }: { falhas: FalhaDaFilial[]; total: number; aoFechar: () => void }): ReactNode {
  const { t } = useTranslation('cadastros');
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('crm.filiais.resultadoTitulo', { count: falhas.length })}
      rodape={
        <Botao variante="primario" onClick={aoFechar} data-fechar-resultado-filiais="">
          {t('crm.filiais.entendi')}
        </Botao>
      }
    >
      <div className="flex flex-col gap-3 text-sm" data-resultado-filiais="">
        <p>{textoParcial(t, total - falhas.length, total)}</p>
        <ul className="flex max-h-72 flex-col gap-2 overflow-y-auto">
          {falhasPorMotivo(falhas).map((g, i) => (
            <li key={i} className="min-w-0 rounded-lg border border-borda p-2">
              <p className="font-medium break-words text-tinta-1">{t('crm.filiais.naoVinculadas', { count: g.nomes.length, motivo: textoLivre(g.motivo) })}</p>
              <p className="break-words text-tinta-2">{g.nomes.map((n) => nomePessoa(n)).join(', ')}</p>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}
